/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import FontFamily from "@tiptap/extension-font-family";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import dayjs from "dayjs";
import * as HTMLEntities from "html-entities";
import { nanoid } from "nanoid";
import { Fragment, Node, Slice } from "prosemirror-model";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import sanitizeHTML from "sanitize-html";
import { Instance, Props } from "tippy.js";
import { LinksAtom } from "../../../../atoms/LinksAtom";
import { UserAtom } from "../../../../atoms/UserAtom";
import { ZoomAtomCampaignNotes } from "../../../../atoms/ZoomAtom";
import { HorizontalRule } from "../../../../components/NewEmail/horizontal-rule";
import { debounce } from "../../../../hook/useDebounce";
import { useBrowserAgent } from "../../../../utils/BrowserAgent";
import { fixCursorOnFrontOfTheLink } from "../../../../utils/EmailEditorUtils";
import {
  getSelectedAnchorElement,
  SAFARI_DEFAULT_SELECTION_BACKGROUND_COLOR,
} from "../../../../utils/LinkUtils";
import { notesSanitizeHtmlOptions } from "../../../../utils/SanitizeBerserkerEmail";
import {
  isNotTrimmedURL,
  isURL,
  selectionMarks,
  TiptapAutoLink,
} from "../../../NewEmail/TiptapAutoLink";
import {
  editorChainKeepAlign,
  TiptapMenuHandle,
} from "../../../NewEmail/TiptapMenu";
import { Underline } from "../../../NewEmail/underline";
import { Strike } from "../../../NewEmail/strikethrough";
import EditorToolbar from "../EditorToolbar";
import DashedItem from "./dashed-item";
import DashedList from "./dashed-list";
import NoteTiptapMenu, { useUpdateBubbleMenu } from "./NoteTiptapMenu";
import TaskItem from "./task-item";
import TaskList from "./task-list";
import useLinkTooltips from "./useNoteLinkTooltips";
import juice from "juice";
import { TiptapHeading } from "../../../NewEmail/TiptapHeading";

const REGEX_SEPARATOR = "|||";

const normalContainer = css`
  width: 100%;
  overflow: auto;
`;

const fullScreeContainer = css`
  width: 100%;
`;

function linkify(text: string, linkColor: string, marks: string[]) {
  const matches = text.matchAll(
    /(h|H)ttps?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/g
  );

  const content = [];
  let startPosition = 0;
  const markTypes = marks.map((mark) => ({ type: mark }));

  let next: IteratorResult<RegExpMatchArray, any>;
  while (!(next = matches.next()).done) {
    const currentMatch = next.value;
    const url = currentMatch[0];
    const index = currentMatch.index;
    const preText = text.substr(startPosition, index);
    if (preText)
      content.push({ type: "text", text: preText, marks: markTypes });
    content.push({
      type: "mention",
      attrs: { id: url, label: url, colorLink: linkColor, tooltipId: nanoid() },
      marks: markTypes.concat([{ type: "underline" }]),
    });
    startPosition = index + url.length;
  }

  const posText = text.substr(startPosition, text.length);
  if (posText)
    content.push({
      type: "text",
      text: posText,
      marks: markTypes,
    });

  return content;
}

interface EmailBodyProps {
  fullScreen: boolean;
  isCampaign?: boolean;
  isResend?: boolean;
}

const EmailBody = styled.div<EmailBodyProps>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  overflow-x: auto;
  ${normalContainer};

  @media only screen and (max-width: 1000px) {
    width: 100%;
  }

  #subject {
    ::placeholder {
      color: #979797;
    }
  }

  ${(props) =>
    !props.isResend &&
    css`
      div[id^="tippy"] {
        z-index: 9998 !important;
      }
      #tippy-1 {
        z-index: 9998 !important;
      }
    `}
`;

const EmailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Body = styled.div<{ isCampaign: boolean; webkit: boolean }>`
  width: 100%;
  font-size: 14px;
  padding: 5px;
  color: #2f2f2f;
  height: 80%;
  line-height: 1.5em;
  border: none;
  border-radius: 0px;
  background-color: white;
  cursor: text;

  p {
    margin: 0px 0px 10px;
    white-space: pre-wrap;
  }

  .ProseMirror {
    padding-bottom: 200px;
  }

  /* Placeholder (at the top) */
  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    float: left;
    pointer-events: none;
    height: 0;
  }

  .ProseMirror-hideselection a::selection {
    background: ${({ webkit }) =>
      webkit
        ? `${SAFARI_DEFAULT_SELECTION_BACKGROUND_COLOR} !important`
        : "default"};
  }

  .ProseMirror-hideselection,
  .ProseMirror-hideselection * {
    caret-color: ${({ webkit }) => (webkit ? "black !important" : "default")};
  }

  .ProseMirror a {
    user-select: ${({ webkit }) => (webkit ? "all !important" : "default")};
    -webkit-user-select: ${({ webkit }) =>
      webkit ? "all !important" : "default"};
  }
`;

export interface EditorHandle {
  getEditor: () => Editor | null;
}

interface NoteEditorProps {
  disabled?: boolean;
  notes: string;
  setNotes: React.Dispatch<React.SetStateAction<string>>;
  campaignLastNotesUpdate?: Date;
}

const NoteEditor: React.ForwardRefRenderFunction<
  EditorHandle,
  NoteEditorProps
> = ({ disabled, notes, setNotes, campaignLastNotesUpdate }, editorRef) => {
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const user = useRecoilValue(UserAtom);
  const zoom = useRecoilValue(ZoomAtomCampaignNotes);
  const { isWebKit } = useBrowserAgent();

  // As it`s used inside the editor callback function, it`s necessary to wrap it inside a ref.
  const links = useRecoilValue(LinksAtom);
  const linksRef =
    useRef<(url: string, from: number, to: number, marks: string[]) => any>();

  const currentLinkQuery = useRef("");
  const currentLinkCommandFunc = useRef<Function | null>(null);
  const colorLinkRef = useRef<string>(
    user?.emailOptions?.defaultModeColorLink || "#000000"
  );
  const currentEditorRef = useRef<Editor | null>(null);

  // Link color behavior
  const changeColorLinkInEditorRef = useRef(null);

  const zoomedRate = parseInt(zoom.name.slice(0, -1), 10) / 100;
  const zoomedBodyFontSize =
    (user.emailOptions.font.size || 14) * zoomedRate + 4;

  function changeColorLinkInEditor() {
    colorLinkRef.current = user?.emailOptions.defaultModeColorLink;
    const autoLinkExtension =
      currentEditorRef.current?.extensionManager.extensions.find(
        ({ name }) => name === "mention"
      )?.options.HTMLAttributes;
    if (autoLinkExtension) {
      autoLinkExtension.colorLink = colorLinkRef.current;
      currentEditorRef.current.commands.setContent(
        currentEditorRef.current.getHTML()
      );
    }
  }

  changeColorLinkInEditorRef.current = changeColorLinkInEditor;

  // Link tooltips behavior
  const tooltipInstances = useRef<{ [href: string]: Instance<Props> }>({});
  const tiptapMenuHandlerRef = useRef<TiptapMenuHandle>(null);
  const tiptapMenu = tiptapMenuHandlerRef.current?.getTiptapMenu();

  useLinkTooltips(
    currentEditorRef.current,
    tooltipInstances,
    tiptapMenu,
    colorLinkRef.current,
    notes
  );

  useEffect(() => {
    if (changeColorLinkInEditorRef.current)
      changeColorLinkInEditorRef.current();
  }, [user?.emailOptions.defaultModeColorLink]);

  useEffect(() => {
    let fixCursorOnFrontOfTheLinkHandler = fixCursorOnFrontOfTheLink();

    return () => {
      fixCursorOnFrontOfTheLinkHandler.clearListener();
    };
  });

  // Link and draft handling
  useEffect(() => {
    linksRef.current = (
      url: string,
      from: number,
      to: number,
      marks: string[]
    ) => {
      if (url) {
        let queriedURL = "";

        queriedURL = queryString.stringifyUrl({
          url,
          query: { __position__: nanoid() },
        });

        currentEditorRef.current.commands.addURLInRange({
          marks,
          from,
          to,
          id: queriedURL,
          label: url,
          colorLink: user?.emailOptions.defaultModeColorLink,
          autoBoldLinks: !!user?.emailOptions.autoBoldLinks,
          tooltipId: nanoid(),
        });
      }
    };
  }, [links, notes, user?.emailOptions.defaultModeColorLink]);

  const { updateMenuAtom } = useUpdateBubbleMenu();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: false,
        codeBlock: false,
        heading: false,
        bold: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
        italic: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
        listItem: {
          HTMLAttributes: {
            style: "margin: 15px 0px;",
          },
        },
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Enter content…",
      }),
      HorizontalRule,
      Underline.configure({
        HTMLAttributes: {
          style: "font-size: inherit;",
          color: colorLinkRef.current,
        },
      }),
      Strike.configure({
        HTMLAttributes: { style: "font-size: inherit;" },
      }),
      TiptapHeading.configure({
        levels: [1, 2, 3],
        HTMLAttributes: {
          style: "line-height: 1.4; margin: 0px;",
        },
      }),
      Document.extend({
        // Overwrite the sortcuts so they are recognized on the Floating Menu, yes, I know...
        addKeyboardShortcuts() {
          return {
            "Mod-b": () => {
              const selectedAnchor: HTMLAnchorElement | null =
                getSelectedAnchorElement();

              if (selectedAnchor) {
                const boldByStyle =
                  selectedAnchor.style.fontWeight &&
                  /^(bold(er)?|[5-9]\d{2,})$/.test(
                    selectedAnchor.style.fontWeight as string
                  );

                let boldByDecoration: any = selectedAnchor.parentNode;
                let isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent

                if (!isBoldByDecoration) {
                  boldByDecoration = boldByDecoration.parentNode;
                  isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
                }

                if (!isBoldByDecoration) {
                  boldByDecoration = boldByDecoration.parentNode;
                  isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
                }

                if (isBoldByDecoration && boldByStyle) {
                  selectedAnchor.style.fontWeight = "none !important";
                  this.editor.chain().focus().unsetBold().run();
                } else if (isBoldByDecoration) {
                  this.editor.chain().focus().unsetBold().run();
                } else if (boldByStyle) {
                  selectedAnchor.style.fontWeight = "none !important";
                  this.editor.chain().focus().unsetBold().run();
                } else {
                  this.editor.chain().focus().toggleBold().run();
                }
              } else {
                this.editor.chain().focus().toggleBold().run();
              }
              return true;
            },
            "Mod-u": () => {
              const selectedAnchor: HTMLAnchorElement | null =
                getSelectedAnchorElement();

              if (selectedAnchor) {
                const underlinedByStyle =
                  selectedAnchor.style.textDecoration === "underline";

                let underlinedByDecoration: any = selectedAnchor.parentNode;
                let isUnderlinedByDecoration =
                  underlinedByDecoration.nodeName === "U"; // replace with direct parent

                if (isUnderlinedByDecoration && underlinedByStyle) {
                  selectedAnchor.style.textDecoration = "none !important";
                  this.editor.chain().focus().unsetUnderline().run();
                } else if (isUnderlinedByDecoration) {
                  this.editor.chain().focus().unsetUnderline().run();
                } else if (underlinedByStyle) {
                  selectedAnchor.style.textDecoration = "none !important";
                  this.editor.chain().focus().unsetUnderline().run();
                } else {
                  this.editor.chain().focus().toggleUnderline().run();
                }
              } else {
                this.editor.chain().focus().toggleUnderline().run();
              }
              return true;
            },
            "Mod-i": () => {
              const selectedAnchor: HTMLAnchorElement | null =
                getSelectedAnchorElement();

              if (selectedAnchor) {
                const italicByStyle =
                  selectedAnchor.style.fontStyle === "italic";

                let italicByDecoration: any = selectedAnchor.parentNode;
                let isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent

                if (!isItalicByDecoration) {
                  italicByDecoration = italicByDecoration.parentNode;
                  isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent
                }

                if (isItalicByDecoration && italicByStyle) {
                  selectedAnchor.style.fontStyle = "none !important";
                  this.editor.chain().focus().unsetItalic().run();
                } else if (isItalicByDecoration) {
                  this.editor.chain().focus().unsetItalic().run();
                } else if (italicByStyle) {
                  selectedAnchor.style.fontStyle = "none !important";
                  this.editor.chain().focus().unsetItalic().run();
                } else {
                  this.editor.chain().focus().toggleItalic().run();
                }
              } else {
                this.editor.chain().focus().toggleItalic().run();
              }
              return true;
            },
            "Mod-Alt-1": () => {
              updateMenuAtom();

              let chain = this.editor.chain().toggleHeading({ level: 1 });
              editorChainKeepAlign(this.editor, chain);
              return chain.run();
            },
            "Mod-Alt-2": () => {
              updateMenuAtom();

              let chain = this.editor.chain().toggleHeading({ level: 2 });
              editorChainKeepAlign(this.editor, chain);
              return chain.run();
            },
            "Mod-Alt-3": () => {
              updateMenuAtom();

              let chain = this.editor.chain().toggleHeading({ level: 3 });
              editorChainKeepAlign(this.editor, chain);
              return chain.run();
            },
            "Mod-Alt-L": () => {
              updateMenuAtom();
              return this.editor.commands.setTextAlign("left");
            },
            "Mod-Alt-E": () => {
              updateMenuAtom();
              return this.editor.commands.setTextAlign("center");
            },
            "Mod-Alt-R": () => {
              updateMenuAtom();
              return this.editor.commands.setTextAlign("right");
            },
            "Mod-Alt-J": () => {
              updateMenuAtom();
              return this.editor.commands.setTextAlign("justify");
            },
          };
        },
        onSelectionUpdate() {
          updateMenuAtom();
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Paragraph.configure({
        HTMLAttributes: {
          style: "margin: 0px",
        },
      }),
      TiptapAutoLink.configure({
        HTMLAttributes: {
          class: "mention",
          colorLink: colorLinkRef.current,
        },
        suggestion: {
          items: (query) => {
            return [];
          },
          render: () => {
            return {
              onStart: (props) => {
                currentLinkQuery.current = props.query;
                currentLinkCommandFunc.current = props.command;
              },
              onUpdate(props) {
                currentLinkQuery.current = props.query;
                currentLinkCommandFunc.current = props.command;
              },
              onExit({ command, query, text, range, marks }) {
                let positionMovingTo =
                  currentEditorRef.current.state.selection.$anchor;

                const { from, to } = range;

                let node = null;
                try {
                  node = currentEditorRef.current.state.doc.nodeAt(from);
                } catch {}

                const nodeText = node?.textContent;
                // It's being fired sometimes, so I'm checking it before continuing
                if (!nodeText) return;
                const nodePosition = nodeText.indexOf(text) + text.length;
                const substringText = nodeText.substring(nodePosition);
                const words = substringText.split(/[\s,]+/);
                const firstOccurrence = words[0];

                const newFrom = to - text.length;
                const newTo = to + firstOccurrence.length;

                // Move the cursor to the next empty space, taking the cursor to the
                // end of the word (https://goo|gle.com => https://google.com|)
                if (command) {
                  let url = `h${query}${firstOccurrence}`;

                  if (!isURL(url)) return;

                  // This changes the position, so I need to revert back
                  // to normal. Also, it's wrapped in a setTimeout to avoid
                  // racing with the tiptap core keyboard listener. It's a temporaty
                  // solution though, we can make it more sophisticated by introducing
                  // two event callbacks on the suggestions plugin!
                  setTimeout(() => {
                    linksRef.current(url, newFrom, newTo, marks);

                    let positionImAt =
                      currentEditorRef.current.state.selection.$anchor;

                    //If you put the cursor after the mention, we need to remove url.length on position
                    if (positionMovingTo.pos > positionImAt.pos) {
                      currentEditorRef.current.commands.focus(
                        positionMovingTo.pos - url.length + 1
                      );
                    } else {
                      currentEditorRef.current.commands.focus(
                        positionMovingTo.pos
                      );
                    }
                  }, 50);
                }
              },
            };
          },
        },
      }),
      Text,
      FontFamily,
      BulletList,
      TaskList.configure({
        itemTypeName: "taskItem",
        HTMLAttributes: {},
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {},
      }),
      DashedList.configure({
        itemTypeName: "dashedItem",
        HTMLAttributes: {},
      }),
      DashedItem.configure({
        nested: true,
        HTMLAttributes: {},
      }),
    ],
    content: sanitizeHTML(notes, notesSanitizeHtmlOptions),

    editorProps: {
      transformPastedHTML: (html) => {
        let result = juice(html);

        let parsed = sanitizeHTML(result, notesSanitizeHtmlOptions);
        return parsed;
      },
      transformPastedText: (text) => {
        let formattedText = text.trim();
        formattedText = sanitizeHTML(formattedText, notesSanitizeHtmlOptions);
        formattedText = HTMLEntities.decode(formattedText);
        return formattedText;
      },
      clipboardTextParser: (text, context) => {
        text = text.replaceAll("\r\n", "\n");
        text = text.replaceAll(/^( |\t|\f)*\r?\n/gm, REGEX_SEPARATOR);
        text = text.replaceAll(`\n${REGEX_SEPARATOR}`, REGEX_SEPARATOR);
        let blocks = text.split(REGEX_SEPARATOR);
        blocks = blocks
          .map((b, index) =>
            index < blocks.length - 1 && b.length ? [b, REGEX_SEPARATOR] : b
          )
          .flat();
        blocks = blocks.map((b) => b.split("\n")).flat();

        const nodes = [];
        const {
          state,
          state: { schema },
        } = currentEditorRef.current;

        let marks = [];
        const collapsed = window.getSelection().isCollapsed;
        if (collapsed) {
          marks = selectionMarks(state, schema);
        }

        blocks.forEach((line) => {
          if (line === REGEX_SEPARATOR) {
            const extraLineNode = Node.fromJSON(context.doc.type.schema, {
              type: "paragraph",
            });
            nodes.push(extraLineNode);
          } else {
            let nodeJson: { [key: string]: any } = { type: "paragraph" };
            if (line.length > 0) {
              nodeJson.content = linkify(
                line,
                colorLinkRef.current || "#000000",
                marks
              );
            }
            const node = Node.fromJSON(context.doc.type.schema, nodeJson);
            nodes.push(node);
          }
        });

        const fragment = Fragment.fromArray(nodes);
        return Slice.maxOpen(fragment);
      },
      transformPasted: (slice: Slice) => {
        slice.content.descendants((node) => {
          if (
            node.type.name === "mention" &&
            isNotTrimmedURL(node.attrs.label || "")
          )
            node.marks = [
              ...node.marks,
              currentEditorRef.current.schema.marks["underline"].create(),
            ];
        });
        return slice;
      },
      handlePaste: (view, e: ClipboardEvent, slice) => {
        // checkEmailBodyAsFirstLineRef.current(view, e, slice);
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      if (setEmailFunc.current) setEmailFunc.current();
    },
    parseOptions: {
      preserveWhitespace: "full",
    },
  });

  const setEmailFunc = useRef<() => void | null>(null);
  setEmailFunc.current = useCallback(
    debounce(() => {
      if (editor) setNotes(editor.getHTML());
    }, 500),
    [editor]
  );

  useEffect(() => {
    if (!editor) return null;

    currentEditorRef.current = editor;

    if (disabled) {
      editor.setEditable(false);
    }
  }, [editor]);

  useImperativeHandle(
    editorRef,
    () => ({
      getEditor: () => editor,
    }),
    [editor]
  );

  if (!user) return null;

  const defaultWrapperWidth = "100%";

  return (
    <>
      <EditorToolbar editor={editor} tooltips={tooltipInstances} isNoteEditor />
      <div className="date-wrapper">
        Last edited on{" "}
        {dayjs(campaignLastNotesUpdate).format("MMM D, YYYY [@] h:mm a (z)")}
      </div>
      <EmailBody
        isResend={false}
        isCampaign={false}
        fullScreen={false}
        css={css`
          height: 480px;
          padding: 12.5px 0 40px;
          background-color: #ffff;

          @media screen and (max-height: 720px) {
            height: 400px;
          }

          @media screen and (max-height: 640px) {
            height: 320px;
          }

          @media screen and (max-width: 1024px) {
            height: 100%;
            padding: 40px 25px;
          }

          .close-button {
            width: 20px;
            height: 20px;

            display: block;
            position: absolute;
            top: 16px;
            right: 16px;

            svg {
              width: 100%;
              height: 100%;
            }
          }
        `}
      >
        {/* I'm hiding the TiptapMenu because of the new Toolbar implementation. So, if Troy wants it back
        someday, we just need to change from "false" to "editor" in this AND below. */}
        {false && (
          <NoteTiptapMenu
            editor={editor}
            ref={tiptapMenuHandlerRef}
            tooltips={tooltipInstances}
          />
        )}
        <EmailWrapper
          css={css`
            margin: 40px auto 0 0;
            align-items: flex-start;
            width: 100%;
          `}
        >
          {editor && (
            <Body
              webkit={isWebKit}
              css={css`
                max-width: ${defaultWrapperWidth};
                width: ${defaultWrapperWidth};
                padding: 5px 0px;
                overflow-y: none;

                font-size: ${zoomedBodyFontSize}px;
                line-height: 24px;
                font-weight: 400;
                color: #2f2f2f;
                letter-spacing: 0.025em;

                flex: 1;

                background-color: #ffff;

                ul[data-type="taskList"] li {
                  display: flex;
                  align-items: flex-start;
                }

                ul[data-type="taskList"] li + li {
                  margin-top: 2px;
                }

                ul[data-type="dashedList"] {
                  list-style-type: none;
                  > li {
                    margin: 15px 0px;
                  }
                  > li::before {
                    content: "_";
                    position: absolute;
                    margin-left: -18px;
                    margin-top: -6px;
                  }
                }

                * {
                  font-family: "Source Sans Pro", sans-serif;
                }

                .is-editor-empty:first-of-type::before {
                  color: #00000040 !important;
                }

                hr {
                  border-color: #ababab;
                }
              `}
              onClick={() => {
                editor.commands.focus();
              }}
              ref={editorContainerRef}
              isCampaign={false}
            >
              <EditorContent editor={editor} />
            </Body>
          )}
        </EmailWrapper>
      </EmailBody>
    </>
  );
};

export default React.forwardRef(NoteEditor);
